import { useEffect, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { Typography } from 'src/components/new/Typography';
import { apiInstance } from 'src/api/apiInstance';

interface GenerationProgressData {
  jobId: number;
  jobStatus: string;
  totalExpected: number;
  totalGenerated: number;
  totalPrinted: number;
  generationProgress: number;
  printProgress: number;
  isGenerationComplete: boolean;
}

interface PrintGenerationProgressProps {
  jobId: number;
}

const PrintGenerationProgress = ({ jobId }: PrintGenerationProgressProps) => {
  const [data, setData] = useState<GenerationProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        const response = await apiInstance.get(`/job/item/${jobId}/generation-progress`);
        setData(response.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [jobId]);

  if (loading) {
    return (
      <>
        <Typography
          sx={{
            marginTop: '0.9375rem',
            marginBottom: '0.9375rem',
            fontWeight: '600',
          }}
        >
          Generation Progress
        </Typography>
        <div
          style={{
            padding: '0.9375rem',
            border: '0.0625rem solid #cac4d0',
            borderRadius: '0rem',
          }}
        >
          <Typography sx={{ color: '#7E828B' }}>Loading...</Typography>
        </div>
      </>
    );
  }

  if (error || !data) {
    return null;
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_queue':
        return 'In Queue';
      case 'processing':
        return 'Processing';
      case 'printing':
        return 'Printing';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'on_hold':
      case 'on_hold_manual':
        return 'On-Hold';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_queue':
        return '#7B3F07';
      case 'processing':
      case 'printing':
        return '#140838';
      case 'completed':
        return '#0F3010';
      case 'cancelled':
        return '#49454F';
      case 'on_hold':
      case 'on_hold_manual':
        return '#5E0A05';
      default:
        return '#7E828B';
    }
  };

  return (
    <>
      <Typography
        sx={{
          marginTop: '0.9375rem',
          marginBottom: '0.9375rem',
          fontWeight: '600',
        }}
      >
        Generation Progress
      </Typography>
      <div
        style={{
          padding: '0.9375rem',
          border: '0.0625rem solid #cac4d0',
          borderRadius: '0rem',
        }}
      >
        <Box sx={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Typography sx={{ fontWeight: '500' }}>Status:</Typography>
          <Typography sx={{ color: getStatusColor(data.jobStatus) }}>
            {getStatusLabel(data.jobStatus)}
          </Typography>
        </Box>

        <Box sx={{ marginBottom: '1rem' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: '500' }}>
              Generation ({data.totalGenerated}/{data.totalExpected})
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#7E828B' }}>
              {data.generationProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.generationProgress}
            sx={{
              height: '0.5rem',
              borderRadius: '0.25rem',
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: '0.25rem',
                backgroundColor: data.isGenerationComplete ? '#2e7d32' : '#1976d2',
              },
            }}
          />
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: '500' }}>
              Printing ({data.totalPrinted}/{data.totalExpected})
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#7E828B' }}>
              {data.printProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.printProgress}
            sx={{
              height: '0.5rem',
              borderRadius: '0.25rem',
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: '0.25rem',
                backgroundColor: data.printProgress === 100 ? '#2e7d32' : '#1976d2',
              },
            }}
          />
        </Box>
      </div>
    </>
  );
};

export default PrintGenerationProgress;
